import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Grid, Stack } from '@mui/material'
import { THRESHOLDS } from 'alerting/constants/AlertConstants'
import Checkbox from 'components/forms/Checkbox'
import Input from 'components/forms/Input'
import NumberField from 'components/forms/NumberField'
import HydrometryAction from 'hydrometry/actions/HydrometryAction'
import PluviometryAction from 'pluviometry/actions/PluviometryAction'
import PropTypes from 'prop-types'
import React, { useMemo, useState } from 'react'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import i18n from 'simple-react-i18n'
import PiezometerStationAction from 'station/actions/PiezometerStationAction'
import StationAction from 'station/actions/StationAction'
import { getThresholdColorCode, getThresholdColorCodes } from 'utils/ColorUtil'
import { getHardPiezoDataTypes } from 'utils/PiezometryUtils'
import { execByType } from 'utils/StationUtils'
import { getLabel } from 'utils/StoreUtils'
import { MEASURE_COTE } from '../../../../piezometry/constants/PiezometryConstants'
import { hasValue, round } from '../../../../utils/NumberUtil'
import ColorPicker from '../../../../components/forms/ColorPicker'
import Select from 'components/forms/Select'
import { CardTable } from 'components/datatable/NewTable'
import { CheckBox, CheckBoxOutlineBlank } from '@mui/icons-material'
import { THRESHOLD_CATEGORIES, THRESHOLD_CATEGORIES_SELECTION } from 'station/constants/StationConstants'
import { getMonthKey } from 'components/echart/EChartUtils'
import { range, chunk, zip } from 'lodash'
import { getMonthList } from 'utils/DateUtil'

const ColorModal = ({
    setThreshold = () => { },
    threshold = {},
}) => {
    const [isOpen, setIsOpen] = useState(false)
    if (!isOpen) {
        const color = getThresholdColorCode(threshold.color)
        return (
            <span
                className='arrests-level-panel clickable'
                style={{ color, 'background-color': color, border: '1px solid black' }}
                onClick={() => setIsOpen(true)}
            >
                O
            </span>
        )
    }
    const elements = getThresholdColorCodes().map((c, i) => (
        <Grid size={1} key={c}>
            <span
                className='arrests-level-panel modal-close clickable'
                style={{ color: c, backgroundColor: c, border: '1px solid black' }}
                onClick={() => {
                    setThreshold(p => ({ ...p, color: i }))
                    setIsOpen(false)
                }}
            >O</span>
        </Grid>
    ))
    return (
        <Grid container>
            {elements}
        </Grid>
    )
}

ColorModal.propTypes = {
    setThreshold: PropTypes.func,
    threshold: PropTypes.shape({}),
}

const ThresholdsTab = ({
    thresholds = [],
    station = {},
    onReload = () => { },
    withoutDataTypeAnnotation = false,
    selectedDataType, // String
}) => {
    const dispatch = useDispatch()

    const {
        accountUser,
        hydrometryDataTypes,
        piezometryDataTypes,
        pluviometryDataTypes,
        pluviometerThresholds,
    } = useSelector(store => ({
        accountUser: store.AccountReducer.accountUser,
        hydrometryDataTypes: store.HydrometryReducer.hydrometryDataTypes,
        piezometryDataTypes: store.PiezometryReducer.piezometryDataTypes,
        pluviometryDataTypes: store.PluviometryReducer.pluviometryDataTypes,
        pluviometerThresholds: store.PluviometryReducer.pluviometerThresholds,
    }), shallowEqual)

    const [isOpen, setIsOpen] = useState(false)
    const [threshold, setThreshold] = useState({
        code: '',
        color: 4,
        value: 0,
        dataType: selectedDataType ? selectedDataType : (station.typeName === 'piezometry' ? '-1' : (station.typeName === 'hydrometry' ? '4' : '1')),
        isOverrunThreshold: '0',
        name: '',
    })

    const fetchThreshold = stationType => {
        switch (stationType) {
            case 'hydrometry':
                dispatch(HydrometryAction.fetchHydrometricThresholds())
                break
            case 'pluviometry':
                dispatch(PluviometryAction.fetchPluviometerAllThresholds())
                break
            default:
                dispatch(PiezometerStationAction.fetchAllPiezometerThreshold(MEASURE_COTE.DEPTH))
                dispatch(PiezometerStationAction.fetchPiezometerThresholds(station.id))
        }
    }

    const dataTypes = execByType(station.typeName, {
        pluviometry: () => pluviometryDataTypes,
        piezometry: () => [...getHardPiezoDataTypes(), ...piezometryDataTypes],
        hydrometry: () => [...hydrometryDataTypes],
        default: () => [],
    })
    const dataType = selectedDataType || threshold.dataType
    const isMonth = useMemo(() => range(1, 13).some(m => hasValue(threshold[getMonthKey(m)])), [threshold])

    const isAuthorize = accountUser.isAdmin === '1' || accountUser.metadata === '1'
    const deleteThreshold = thresh => {
        const newThresholds = [...thresholds.filter(t => t.id !== thresh.id)]
        const afterDelete = () => {
            fetchThreshold(station.typeName)
            onReload(station, THRESHOLDS)
        }
        if (station.typeName === 'pluviometry') {
            const currentDataType = selectedDataType ?? thresh.dataType
            const untouchedForOtherTypes = pluviometerThresholds.filter(t => `${t.dataType}` !== `${currentDataType}`)
            dispatch(StationAction.updateThresholds(station.id, [...untouchedForOtherTypes, ...newThresholds], 'pluviometry')).then(afterDelete)
        } else {
            dispatch(StationAction.updateThresholds(station.id, newThresholds, station.typeName, MEASURE_COTE.DEPTH)).then(afterDelete)
        }
    }
    const thresholdsFormated = thresholds.map(t => {
        const color = t.htmlColor ?? getThresholdColorCode(t.color)
        const isMonthThreshold = range(1, 13).some(m => hasValue(t[getMonthKey(m)]))
        return {
            id: t.id,
            nullValue: (
                <span style={{ color, backgroundColor: color, border: color === '#FFFFFF' ? '1px solid black' : undefined, borderRadius: '10px', padding: '0px 6px' }}>OO</span>
            ),
            name: t.name,
            value: isMonthThreshold ? i18n.isMonthThreshold : round(t.value, 5),
            dataType: getLabel(dataTypes, parseInt(t.dataType), 'label', 'id'),
            thresholdOverrun: {
                sortValue: t.isOverrunThreshold === '1',
                value: t.isOverrunThreshold === '1' ? <CheckBox /> : <CheckBoxOutlineBlank />,
            },
        }
    })
    const reset = () => {
        setIsOpen(false)
        setThreshold({
            code: '',
            color: 4,
            value: 0,
            dataType: selectedDataType ? selectedDataType : (station.typeName === 'piezometry' ? '-1' : (station.typeName === 'hydrometry' ? '4' : '1')),
            isOverrunThreshold: '0',
            name: '',
        })
    }
    const months = useMemo(() => range(1, 13).map(m => {
        const monthKey = getMonthKey(m)
        return (
            <Grid size={3} key={`${threshold.id}-${m}`}>
                <NumberField title={getMonthList()[m-1].name}
                    value={threshold[monthKey]}
                    onChange={v => setThreshold(prev => ({ ...prev, [monthKey]: v }))}
                    floatValue
                />
            </Grid>
        )
    }), [threshold])
    const groupsMonth = chunk(months, 3)
    const values = Reflect.apply(zip, {}, groupsMonth)
    return (
        <>
            <CardTable
                title={i18n.thresholds}
                rows={thresholdsFormated}
                actions={isAuthorize ? [{
                    onClick: () => setIsOpen(true),
                    icon: 'note_add',
                }] : []}
                lineActions={[
                    {
                        icon: 'edit',
                        onClick: ({ id }) => {
                            const thresholdFound = thresholds.find(t => t.id === id)
                            setThreshold(thresholdFound)
                            setIsOpen(true)
                        },
                    },
                    {
                        icon: 'close',
                        onClick: ({ id }) => {
                            const thresholdFound = thresholds.find(t => t.id === id)
                            deleteThreshold(thresholdFound)
                        },
                    },
                ]}
                headers={withoutDataTypeAnnotation ? ['nullValue', 'name', 'value', 'thresholdOverrun'] : ['nullValue', 'name', 'value', 'dataType', 'thresholdOverrun']}
                id='thresholds_table'
            />

            <Dialog
                fullWidth
                maxWidth='lg'
                onClose={reset}
                open={isOpen}
            >
                <DialogTitle>{i18n.addOrUpdateThreshold}</DialogTitle>
                <DialogContent>
                    <Stack spacing={1}>
                        <Grid container spacing={1}>
                            <Grid size={3}>
                                <Input
                                    value={threshold.name}
                                    title={i18n.title}
                                    onChange={v => setThreshold(p => ({ ...p, name: v }))}
                                    obligatory
                                />
                            </Grid>
                            <Grid size={3}>
                                <NumberField
                                    value={round(threshold.value, 5)}
                                    title={i18n.value}
                                    floatValue
                                    disabled={isMonth}
                                    onChange={v => setThreshold(p => ({ ...p, value: v }))}
                                />
                            </Grid>
                            <Grid size={3}>
                                <Select
                                    label={i18n.type}
                                    options={dataTypes}
                                    value={dataType}
                                    keyValue='id'
                                    keyLabel='label'
                                    onChange={v => setThreshold(p => ({ ...p, dataType: v.toString() }))}
                                    obligatory
                                    disabled={!!selectedDataType}
                                />
                            </Grid>
                            <Grid size={3}>
                                <Select
                                    label={i18n.category}
                                    options={THRESHOLD_CATEGORIES_SELECTION}
                                    value={threshold.category || THRESHOLD_CATEGORIES.SITUATION}
                                    noNullValue
                                    keyValue='code'
                                    keyLabel='name'
                                    onChange={v => setThreshold(p => ({ ...p, category: v }))}
                                    obligatory
                                    noSort
                                />
                            </Grid>
                        </Grid>
                        <Grid container spacing={1} alignItems={'center'}>
                            <Grid size={2}>
                                <Checkbox
                                    label={i18n.thresholdOverrun}
                                    checked={threshold.isOverrunThreshold === '1'}
                                    onChange={e => setThreshold(p => ({ ...p, isOverrunThreshold: e ? '1' : '0' }))}
                                />
                            </Grid>
                            <Grid size={2}>
                                <Checkbox
                                    label={i18n.isMonthThreshold}
                                    checked={isMonth}
                                    onChange={isChecked => {
                                        if (isChecked) {
                                            setThreshold(prev => ({ ...prev, value: null, month01: threshold.value || 1 }))
                                        } else {
                                            const nullMonth = range(1, 13).reduce((acc, m) => ({ ...acc, [getMonthKey(m)]: undefined }), {})
                                            setThreshold(prev => ({ ...prev, value: prev.month01, ...nullMonth }))
                                        }
                                    }}
                                />
                            </Grid>
                            <Grid size={3}>
                                <ColorPicker
                                    label={i18n.color}
                                    value={threshold.htmlColor ?? getThresholdColorCode(threshold.color)}
                                    onChange={e => setThreshold(p => ({ ...p, htmlColor: e }))}
                                />
                            </Grid>
                        </Grid>
                        {isMonth && (
                            <Grid container spacing={1}>
                                { values[0]}
                                { values[1]}
                                { values[2]}
                            </Grid>
                        )}
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button
                        variant='outlined'
                        onClick={reset}
                    >
                        {i18n.cancel}
                    </Button>
                    <Button
                        variant='contained'
                        disabled={!threshold.dataType || !threshold.name?.replaceAll(' ', '')?.length}
                        onClick={() => {
                            const editedThreshold = {
                                ...threshold,
                                category: threshold.category || THRESHOLD_CATEGORIES.SITUATION,
                            }
                            const newThresholds = [
                                ...thresholds.filter(t => t.id !== threshold.id),
                                editedThreshold,
                            ]
                            const afterSave = () => {
                                fetchThreshold(station.typeName)
                                onReload(station, THRESHOLDS)
                                reset()
                            }
                            if (station.typeName === 'pluviometry') {
                                const currentDataType = selectedDataType ?? threshold.dataType
                                const editedForCurrentType = newThresholds.map(t => ({ ...t, dataType: `${currentDataType}` }))
                                const untouchedForOtherTypes = pluviometerThresholds.filter(t => `${t.dataType}` !== `${currentDataType}`)
                                dispatch(StationAction.updateThresholds(station.id, [...untouchedForOtherTypes, ...editedForCurrentType], 'pluviometry')).then(afterSave)
                            } else if (selectedDataType) {
                                dispatch(StationAction.updateThresholdsWithDataType(station.id, newThresholds.map(t => ({ ...t, dataType: selectedDataType })), station.typeName, selectedDataType)).then(afterSave)
                            } else {
                                dispatch(StationAction.updateThresholds(station.id, newThresholds, station.typeName, MEASURE_COTE.DEPTH)).then(afterSave)
                            }
                        }}
                    >
                        {i18n.save}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    )
}

ThresholdsTab.propTypes = {
    thresholds: PropTypes.arrayOf(PropTypes.shape({})),
    station: PropTypes.shape({}),
    onReload: PropTypes.func,
    withoutDataTypeAnnotation: PropTypes.bool,
    selectedDataType: PropTypes.string,
}

export default ThresholdsTab